import { Component, input, model, computed, output, signal, effect } from "@angular/core";
import { DialogModule } from "primeng/dialog";
import { ButtonModule } from "primeng/button";
import { TagModule } from "primeng/tag";
import { DividerModule } from "primeng/divider";
import { CardModule } from "primeng/card";
import { AvatarModule } from "primeng/avatar";
import { ChipModule } from "primeng/chip";
import { ProgressBarModule } from "primeng/progressbar";
import { DatePipe, TitleCasePipe } from "@angular/common";
import { Router } from "@angular/router";
import { IUser } from "../../../../Utils/_models/users.model";

@Component({
  selector: "app-user-details-modal",
  standalone: true,
  imports: [
    DialogModule,
    ButtonModule,
    TagModule,
    DividerModule,
    CardModule,
    AvatarModule,
    ChipModule,
    ProgressBarModule,
    DatePipe,
    TitleCasePipe
  ],
  templateUrl: "./user-details-modal.component.html",
  styleUrl: "./user-details-modal.component.css",
})
export class UserDetailsModalComponent {
  user = input<IUser>();
  visible = model<boolean>(false);

  onClose = output();
  avatarImageFailed = false;
  currentPhotoIndex = signal<number>(0);
  isPlaying = signal<boolean>(false);
  private currentAudio: HTMLAudioElement | null = null;
  private currentSpeech: SpeechSynthesisUtterance | null = null;

  constructor(private router : Router){

  }
  ngOnDestroy() {
    this.stopPlayback();
  }

  stopPlayback() {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio = null;
    }
    if (this.currentSpeech) {
      window.speechSynthesis.cancel();
      this.currentSpeech = null;
    }
    this.isPlaying.set(false);
  }

  private initPhotoIndex = effect(() => {
    const userData = this.user();
    if (userData) {
      const photos = userData.profilePhotos || [];
      const primaryIndex = photos.findIndex(p => p.isPrimary);
      this.currentPhotoIndex.set(primaryIndex >= 0 ? primaryIndex : 0);
      this.avatarImageFailed = false;

      // Preload all photos in the background for instant navigation
      photos.forEach(photo => {
        if (photo.signedUrl) {
          const img = new Image();
          img.src = photo.signedUrl;
        }
      });
    }
  });

fullName = computed(() => {
  const userData = this.user();

  if (!userData) return 'N/A';

  const firstName = userData.firstName?.trim() ?? '';
  const lastName = userData.lastName?.trim() ?? '';

  const name = `${firstName} ${lastName}`.trim();

  return name || 'N/A';
});
  location = computed(() => {
    const userData = this.user();
    return userData ? `${userData.location?.city}, ${userData.location?.state}, ${userData.location?.country}` : "N/A";
  });

  // netBalance = computed(() => {
  //   const userData = this.user();
  //   if (!userData) return 0;
  //   return (userData.totalEarned || 0) - (userData.totalSpent || 0);
  // });

  getInitials(): string {
    const userData = this.user();
    if (!userData || !userData.firstName) return "N/A";
    return `${userData.firstName[0]}${userData.lastName[0]}`.toUpperCase();
  }


  onAvatarError(event: any): void {
    this.avatarImageFailed = true;
  }

  getProfileImage(): string | undefined {
    if (this.avatarImageFailed) return undefined;
    const photos = this.user()?.profilePhotos || [];
    if (photos.length === 0) return undefined;
    const idx = this.currentPhotoIndex();
    const safeIdx = idx >= 0 && idx < photos.length ? idx : 0;
    return photos[safeIdx]?.signedUrl || undefined;
  }

  prevPhoto(event: MouseEvent): void {
    event.stopPropagation();
    const photos = this.user()?.profilePhotos || [];
    if (photos.length <= 1) return;
    this.avatarImageFailed = false;
    this.currentPhotoIndex.update(idx => (idx - 1 + photos.length) % photos.length);
  }

  nextPhoto(event: MouseEvent): void {
    event.stopPropagation();
    const photos = this.user()?.profilePhotos || [];
    if (photos.length <= 1) return;
    this.avatarImageFailed = false;
    this.currentPhotoIndex.update(idx => (idx + 1) % photos.length);
  }

  openImageInNewTab(event: MouseEvent): void {
    event.stopPropagation();
    const url = this.getProfileImage();
    if (url) {
      window.open(url, "_blank");
    }
  }

  playVoiceRecord(url: string | undefined): void {
    if (this.isPlaying()) {
      this.stopPlayback();
      return;
    }

    this.isPlaying.set(true);

    if (url) {
      this.currentAudio = new Audio(url);
      this.currentAudio.onended = () => {
        this.isPlaying.set(false);
        this.currentAudio = null;
      };
      this.currentAudio.play().catch(err => {
        console.error("Error playing audio", err);
        this.isPlaying.set(false);
      });
    } else {
      const name = this.fullName();
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
        this.currentSpeech = new SpeechSynthesisUtterance(name);
        this.currentSpeech.onend = () => {
          this.isPlaying.set(false);
          this.currentSpeech = null;
        };
        this.currentSpeech.onerror = () => {
          this.isPlaying.set(false);
          this.currentSpeech = null;
        };
        window.speechSynthesis.speak(this.currentSpeech);
      } else {
        this.isPlaying.set(false);
      }
    }
  }

  closeDialog(): void {
    this.stopPlayback();
    this.onClose.emit();
  }
  getAvatarColor(): string {
    const colors = [
      "#857031",
      "#857031",
      "#857031",
      "#857031",
      "#857031",
      "#857031",
    ];
    const userData = this.user();
    if (!userData) return colors[0];
    const index = userData._id.charCodeAt(0) % colors.length;
    return colors[index];
  }

  getSubscriptionSeverity(subscriptionType: string): string {
    const severityMap: Record<string, string> = {
      premium: "p-tag-success",
      pro: "p-tag-info",
      basic: "p-tag-secondary",
      free: "p-tag-contrast",
    };

    return severityMap[subscriptionType.toLowerCase()] || "p-tag-secondary";
  }



  getGenderIcon(gender: string): string {
    const iconMap: Record<string, string> = {
      male: "pi pi-mars ml-2",
      female: "pi pi-venus ml-2",
      other: "pi pi-user ml-2",
    };
    return iconMap[gender.toLowerCase()] || "pi pi-user ml-2";
  }



  viewFullProfile(): void {
    const userData = this.user();
    if (userData) {
      this.visible.set(false);
      this.router.navigate(['/admin/users', userData._id]);
    }
  }


}
